import React, { useState, useEffect } from "react";
import { db, auth } from "./config/firebaseConfig";
import {
  collection,
  addDoc,
  serverTimestamp,
  getDocs,
  query,
  where,
  orderBy,
  limit
} from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import Navbar from "./Navbar";
import Footer from "./Footer";

const TherapistWeeklySurvey = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [previousResponses, setPreviousResponses] = useState([]);
  const [showPreviousResponses, setShowPreviousResponses] = useState(false);
  
  // Form state
  const [survey, setSurvey] = useState({
    usageFrequency: "",
    usabilityRating: 3,
    featureUsage: {
      exposureHierarchy: false,
      assessmentTools: false,
      triggersTracker: false,
      moodTracker: false,
      goalSetting: false,
      messaging: false
    },
    mostValuableFeature: "",
    leastValuableFeature: "",
    patientFeedback: "",
    difficultiesEncountered: "",
    suggestionForImprovement: "",
    newFeatureRequest: "",
    designFeedback: ""
  });

  // Check if user is logged in
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate("/login");
      } else {
        // Check if user has already submitted feedback this week
        checkPreviousSubmissions(user.uid);
      }
    });
    return () => unsubscribe();
  }, [navigate]);

  // Check previous submissions
  const checkPreviousSubmissions = async (userId) => {
    try {
      const surveyRef = collection(db, "therapistWeeklySurveys");
      const q = query(
        surveyRef,
        where("therapistId", "==", userId),
        orderBy("timestamp", "desc"),
        limit(5)
      );
      
      const querySnapshot = await getDocs(q);
      const submissions = [];
      querySnapshot.forEach((doc) => {
        submissions.push({
          id: doc.id,
          ...doc.data(),
          timestamp: doc.data().timestamp.toDate().toLocaleString()
        });
      });
      
      setPreviousResponses(submissions);
      
      // Check if there's a submission from this week
      if (submissions.length > 0) {
        const latestSubmission = submissions[0];
        const submissionDate = new Date(latestSubmission.timestamp);
        const today = new Date();
        
        // If submission was within the last 7 days, consider it this week's submission
        const diffTime = Math.abs(today - submissionDate);
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays <= 7) {
          setIsSubmitted(true);
        }
      }
    } catch (error) {
      console.error("Error checking previous submissions:", error);
    }
  };

  // Handle input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSurvey({ ...survey, [name]: value });
  };

  // Handle checkbox changes for feature usage
  const handleCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setSurvey({
      ...survey,
      featureUsage: {
        ...survey.featureUsage,
        [name]: checked
      }
    });
  };

  // Handle radio button changes
  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    setSurvey({ ...survey, [name]: value });
  };

  // Handle slider changes
  const handleSliderChange = (e) => {
    const { name, value } = e.target;
    setSurvey({ ...survey, [name]: Number(value) });
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!auth.currentUser) {
      alert("You must be logged in to submit feedback.");
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Get selected feature usage as array
      const featuresUsed = Object.entries(survey.featureUsage)
        .filter(([_, value]) => value)
        .map(([key, _]) => key);
      
      // Prepare survey data
      const surveyData = {
        ...survey,
        featuresUsed,
        therapistId: auth.currentUser.uid,
        therapistEmail: auth.currentUser.email,
        timestamp: serverTimestamp(),
        weekNumber: getWeekNumber(new Date())
      };
      
      // Save to Firestore
      await addDoc(collection(db, "therapistWeeklySurveys"), surveyData);
      
      setIsSubmitted(true);
      setIsSubmitting(false);
      
      // Reset the form
      setSurvey({
        usageFrequency: "",
        usabilityRating: 3,
        featureUsage: {
          exposureHierarchy: false,
          assessmentTools: false,
          triggersTracker: false,
          moodTracker: false,
          goalSetting: false,
          messaging: false
        },
        mostValuableFeature: "",
        leastValuableFeature: "",
        patientFeedback: "",
        difficultiesEncountered: "",
        suggestionForImprovement: "",
        newFeatureRequest: "",
        designFeedback: ""
      });
      
    } catch (error) {
      console.error("Error submitting survey:", error);
      alert("There was an error submitting your feedback. Please try again.");
      setIsSubmitting(false);
    }
  };

  // Helper function to get the current week number
  const getWeekNumber = (date) => {
    const firstDayOfYear = new Date(date.getFullYear(), 0, 1);
    const pastDaysOfYear = (date - firstDayOfYear) / 86400000;
    return Math.ceil((pastDaysOfYear + firstDayOfYear.getDay() + 1) / 7);
  };
  
  // Helper function to toggle previous responses view
  const togglePreviousResponses = () => {
    setShowPreviousResponses(!showPreviousResponses);
  };

  return (
    <>
      <Navbar />
      <div className="survey-container">
        <style>
          {`
            .survey-container {
              max-width: 800px;
              margin: 30px auto;
              padding: 25px;
              background-color: #1e1e1e;
              color: white;
              border-radius: 8px;
              box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
            }
            
            .survey-title {
              color: #f09a73;
              margin-bottom: 20px;
              text-align: center;
            }
            
            .survey-section {
              margin-bottom: 25px;
              padding: 20px;
              background-color: #2e2e2e;
              border-radius: 8px;
            }
            
            .section-title {
              color: #4caf50;
              margin-bottom: 15px;
              font-size: 18px;
              border-bottom: 1px solid #444;
              padding-bottom: 8px;
            }
            
            .form-group {
              margin-bottom: 20px;
            }
            
            .form-group label {
              display: block;
              margin-bottom: 8px;
              font-weight: bold;
            }
            
            .form-group input[type="text"],
            .form-group textarea,
            .form-group select {
              width: 100%;
              padding: 10px;
              background-color: #444;
              border: 1px solid #555;
              border-radius: 4px;
              color: white;
              font-size: 14px;
            }
            
            .form-group textarea {
              min-height: 100px;
              resize: vertical;
            }
            
            .checkbox-group {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
              gap: 10px;
            }
            
            .checkbox-item {
              display: flex;
              align-items: center;
              margin-bottom: 10px;
            }
            
            .checkbox-item input {
              margin-right: 10px;
            }
            
            .radio-group {
              display: flex;
              flex-direction: column;
              gap: 10px;
            }
            
            .radio-item {
              display: flex;
              align-items: center;
            }
            
            .radio-item input {
              margin-right: 10px;
            }
            
            .slider-container {
              margin-top: 10px;
            }
            
            .slider {
              width: 100%;
              margin: 10px 0;
            }
            
            .slider-labels {
              display: flex;
              justify-content: space-between;
              margin-top: 5px;
              font-size: 12px;
              color: #aaa;
            }
            
            .submit-button {
              background-color: #4caf50;
              color: white;
              border: none;
              padding: 12px 20px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 16px;
              transition: background-color 0.3s;
              width: 100%;
            }
            
            .submit-button:hover {
              background-color: #45a049;
            }
            
            .submit-button:disabled {
              background-color: #666;
              cursor: not-allowed;
            }
            
            .success-message {
              text-align: center;
              padding: 30px;
              background-color: rgba(76, 175, 80, 0.2);
              border: 1px solid #4caf50;
              border-radius: 8px;
              margin: 20px 0;
            }
            
            .success-message h2 {
              color: #4caf50;
              margin-bottom: 10px;
            }
            
            .previous-responses {
              margin-top: 30px;
            }
            
            .toggle-button {
              background-color: #333;
              color: white;
              border: 1px solid #555;
              padding: 8px 15px;
              border-radius: 4px;
              cursor: pointer;
              font-size: 14px;
              transition: background-color 0.3s;
            }
            
            .toggle-button:hover {
              background-color: #444;
            }
            
            .previous-response {
              margin-top: 15px;
              padding: 15px;
              background-color: #333;
              border-radius: 4px;
              border-left: 4px solid #f09a73;
            }
            
            .previous-response-date {
              font-size: 12px;
              color: #aaa;
              margin-bottom: 10px;
            }
            
            .previous-response-content {
              font-size: 14px;
            }
            
            .features-used-list {
              display: flex;
              flex-wrap: wrap;
              gap: 5px;
              margin-top: 5px;
            }
            
            .feature-tag {
              background-color: #444;
              padding: 3px 8px;
              border-radius: 3px;
              font-size: 12px;
            }
          `}
        </style>

        <h1 className="survey-title">OCDSerenity Weekly Therapist Feedback</h1>
        
        {isSubmitted ? (
          <div className="success-message">
            <h2>Thank You for Your Feedback!</h2>
            <p>Your input is invaluable in helping us improve OCDSerenity as we continue to develop specialized tools for OCD treatment.</p>
            <p>We review all feedback carefully and use it to prioritize our development efforts.</p>
            
            
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div className="survey-section">
              <h2 className="section-title">Platform Usage</h2>
              
              <div className="form-group">
                <label>How frequently did you use OCDSerenity this week?</label>
                <div className="radio-group">
                  <div className="radio-item">
                    <input 
                      type="radio" 
                      id="daily" 
                      name="usageFrequency" 
                      value="daily"
                      checked={survey.usageFrequency === "daily"}
                      onChange={handleRadioChange}
                      required
                    />
                    <label htmlFor="daily">Daily (5+ days)</label>
                  </div>
                  <div className="radio-item">
                    <input 
                      type="radio" 
                      id="several" 
                      name="usageFrequency" 
                      value="several"
                      checked={survey.usageFrequency === "several"}
                      onChange={handleRadioChange}
                    />
                    <label htmlFor="several">Several times (3-4 days)</label>
                  </div>
                  <div className="radio-item">
                    <input 
                      type="radio" 
                      id="couple" 
                      name="usageFrequency" 
                      value="couple"
                      checked={survey.usageFrequency === "couple"}
                      onChange={handleRadioChange}
                    />
                    <label htmlFor="couple">A couple times (1-2 days)</label>
                  </div>
                  <div className="radio-item">
                    <input 
                      type="radio" 
                      id="not-used" 
                      name="usageFrequency" 
                      value="not-used"
                      checked={survey.usageFrequency === "not-used"}
                      onChange={handleRadioChange}
                    />
                    <label htmlFor="not-used">I did not use it this week</label>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label>Which features did you use with this week? (Select all that apply)</label>
                <div className="checkbox-group">
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="exposureHierarchy" 
                      name="exposureHierarchy"
                      checked={survey.featureUsage.exposureHierarchy}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="exposureHierarchy">Exposure Hierarchy Builder</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="assessmentTools" 
                      name="assessmentTools"
                      checked={survey.featureUsage.assessmentTools}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="assessmentTools">Assessment Tools (Y-BOCS, PHQ-9, GAD-7)</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="triggersTracker" 
                      name="triggersTracker"
                      checked={survey.featureUsage.triggersTracker}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="triggersTracker">Triggers Tracker</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="moodTracker" 
                      name="moodTracker"
                      checked={survey.featureUsage.moodTracker}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="moodTracker">Mood Tracker</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="goalSetting" 
                      name="goalSetting"
                      checked={survey.featureUsage.goalSetting}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="goalSetting">Goal Setting</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="messaging" 
                      name="messaging"
                      checked={survey.featureUsage.messaging}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="messaging">Messaging with Patients</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="journaling" 
                      name="journaling"
                      checked={survey.featureUsage.journaling}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="messaging">Journaling</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="reassurance" 
                      name="reassurance"
                      checked={survey.featureUsage.reassurance}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="messaging">Reassurance Coupon</label>
                  </div>
                  <div className="checkbox-item">
                    <input 
                      type="checkbox" 
                      id="suds" 
                      name="suds"
                      checked={survey.featureUsage.suds}
                      onChange={handleCheckboxChange}
                    />
                    <label htmlFor="messaging">SUDS Tracker</label>
                  </div>
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="usabilityRating">How would you rate the overall usability of the platform this week?</label>
                <div className="slider-container">
                  <input 
                    type="range" 
                    id="usabilityRating" 
                    name="usabilityRating" 
                    min="1" 
                    max="5" 
                    value={survey.usabilityRating}
                    onChange={handleSliderChange}
                    className="slider"
                  />
                  <div className="slider-labels">
                    <span>Difficult to use</span>
                    <span>Neutral</span>
                    <span>Very easy to use</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="survey-section">
              <h2 className="section-title">Feature Feedback</h2>
              
              <div className="form-group">
                <label htmlFor="mostValuableFeature">Which feature did you find most valuable this week and why?</label>
                <textarea 
                  id="mostValuableFeature" 
                  name="mostValuableFeature"
                  value={survey.mostValuableFeature}
                  onChange={handleInputChange}
                  placeholder="Please describe the feature and how it helped your therapeutic work..."
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="leastValuableFeature">Which feature did you find least valuable or most difficult to use?</label>
                <textarea 
                  id="leastValuableFeature" 
                  name="leastValuableFeature"
                  value={survey.leastValuableFeature}
                  onChange={handleInputChange}
                  placeholder="Please describe any features that didn't work well for you..."
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="patientFeedback">What feedback, if any, did you receive from your patients about OCDSerenity?</label>
                <textarea 
                  id="patientFeedback" 
                  name="patientFeedback"
                  value={survey.patientFeedback}
                  onChange={handleInputChange}
                  placeholder="Please share any comments or reactions from your patients..."
                ></textarea>
              </div>
            </div>
            
            <div className="survey-section">
              <h2 className="section-title">Issues & Improvements</h2>
              
              <div className="form-group">
                <label htmlFor="difficultiesEncountered">Did you encounter any technical difficulties or bugs this week?</label>
                <textarea 
                  id="difficultiesEncountered" 
                  name="difficultiesEncountered"
                  value={survey.difficultiesEncountered}
                  onChange={handleInputChange}
                  placeholder="Please describe any issues as specifically as possible..."
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="suggestionForImprovement">What improvements would you suggest for existing features?</label>
                <textarea 
                  id="suggestionForImprovement" 
                  name="suggestionForImprovement"
                  value={survey.suggestionForImprovement}
                  onChange={handleInputChange}
                  placeholder="Please provide specific suggestions for how we could enhance current features..."
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="newFeatureRequest">Are there any new features you'd like to see added to OCDSerenity?</label>
                <textarea 
                  id="newFeatureRequest" 
                  name="newFeatureRequest"
                  value={survey.newFeatureRequest}
                  onChange={handleInputChange}
                  placeholder="Please describe any new tools or features that would enhance your OCD treatment practice..."
                ></textarea>
              </div>
              
              <div className="form-group">
                <label htmlFor="designFeedback">Do you have any feedback about the design or interface?</label>
                <textarea 
                  id="designFeedback" 
                  name="designFeedback"
                  value={survey.designFeedback}
                  onChange={handleInputChange}
                  placeholder="Please share thoughts on colors, layout, organization, etc..."
                ></textarea>
              </div>
            </div>
            
            <button 
              type="submit" 
              className="submit-button"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Submitting..." : "Submit Feedback"}
            </button>
          </form>
        )}
        
        {previousResponses.length > 0 && (
          <div className="previous-responses">
            <button 
              className="toggle-button"
              onClick={togglePreviousResponses}
            >
              {showPreviousResponses ? "Hide Previous Responses" : "Show Previous Responses"}
            </button>
            
            {showPreviousResponses && (
              <div style={{ marginTop: "15px" }}>
                <h3 style={{ fontSize: "16px", color: "#f09a73", marginBottom: "10px" }}>Your Previous Feedback</h3>
                
                {previousResponses.map((response) => (
                  <div key={response.id} className="previous-response">
                    <div className="previous-response-date">
                      Submitted on: {response.timestamp}
                    </div>
                    <div className="previous-response-content">
                      <p><strong>Usage:</strong> {response.usageFrequency}</p>
                      <p><strong>Features Used:</strong></p>
                      <div className="features-used-list">
                        {response.featuresUsed && response.featuresUsed.map((feature, index) => (
                          <span key={index} className="feature-tag">{feature}</span>
                        ))}
                      </div>
                      <p><strong>Most Valuable Feature:</strong> {response.mostValuableFeature}</p>
                      {response.suggestionForImprovement && (
                        <p><strong>Suggested Improvements:</strong> {response.suggestionForImprovement}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      <Footer />
    </>
  );
};

export default TherapistWeeklySurvey;